import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function NotificationBell() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();

  const load = async () => {
    try {
      const r = await api.get("/notifications");
      setItems(r.data);
    } catch {}
  };

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, 15000); // poll every 15s
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    const handle = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  if (!user) return null;
  const unread = items.filter(n => !n.read).length;

  const openNotif = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch {}
    }
    setOpen(false);
    if (n.ref_id) nav(`/case/${n.ref_id}`);
    load();
  };

  const markAll = async () => {
    await Promise.all(items.filter(n => !n.read).map(n => api.post(`/notifications/${n.id}/read`).catch(() => {})));
    load();
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className="relative w-10 h-10 rounded-lg border border-slate-200 hover:bg-slate-50 grid place-items-center text-slate-700"
        data-testid="notif-bell-btn">
        <Bell className="w-5 h-5"/>
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center border-2 border-white" data-testid="notif-unread-count">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden z-50" data-testid="notif-panel">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="font-heading font-semibold text-slate-900">Notifications</div>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs font-semibold text-blue-700 hover:text-blue-800 flex items-center gap-1" data-testid="notif-markall">
                <CheckCheck className="w-3.5 h-3.5"/> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && <div className="px-4 py-8 text-center text-sm text-slate-500">No notifications yet.</div>}
            {items.map(n => (
              <button
                key={n.id}
                onClick={() => openNotif(n)}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 flex gap-3 ${n.read ? "" : "bg-blue-50/40"}`}
                data-testid={`notif-item-${n.id}`}>
                {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-700 flex-shrink-0"/>}
                <div className="min-w-0 flex-1">
                  <div className={`text-sm ${n.read ? "text-slate-600" : "text-slate-900 font-medium"}`}>{n.text}</div>
                  <div className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
